import { lazy, Suspense, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAnalytics } from "@/hooks/useAnalytics";
import ScrollToTop from "@/components/ScrollToTop";
import NavigationProgress from "@/components/NavigationProgress";
import DockedBookingBar, { BookingDockProvider } from "@/components/BookingDock";
import ErrorBoundary from "@/components/ErrorBoundary";
import { LanguageRoute } from "@/components/LanguageRoute";
import Index from "./pages/Index";

// Build stamp for cache/deployment debugging (injected by Vite define)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore – __BUILD_TIME__ is defined in vite.config.ts
const BUILD_ID = `frostflotte-${__BUILD_TIME__}`;
console.info(`[Frostflotte] Build: ${BUILD_ID}`);

// Singleton QueryClient – wraps ALL routes (including "/")
const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1, staleTime: 5 * 60 * 1000 } },
});

const RadixToaster = lazy(() =>
  import("@/components/ui/toaster").then(m => ({ default: m.Toaster }))
);
const SonnerToaster = lazy(() =>
  import("@/components/ui/sonner").then(m => ({ default: m.Toaster }))
);

const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const NotFound = lazy(() => import("./pages/NotFound"));
const QRCodePage = lazy(() => import("./pages/QRCode"));
const PharmaTransport = lazy(() => import("./pages/PharmaTransport"));
const CateringEvents = lazy(() => import("./pages/CateringEvents"));
const LebensmittelTransport = lazy(() => import("./pages/LebensmittelTransport"));
const UeberUns = lazy(() => import("./pages/UeberUns"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const MobileKuehlung = lazy(() => import("./pages/MobileKuehlung"));
const TransporterMieten = lazy(() => import("./pages/TransporterMieten"));
const KleinbusMietenFrankfurt = lazy(() => import("./pages/KleinbusMietenFrankfurt"));
const KuehltransportMitFahrer = lazy(() => import("./pages/KuehltransportMitFahrer"));
const Vertrauen = lazy(() => import("./pages/Vertrauen"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const HaccpLeitfaden = lazy(() => import("./pages/HaccpLeitfaden"));
const StadtLanding = lazy(() => import("./pages/StadtLanding"));
const Standorte = lazy(() => import("./pages/Standorte"));
const EisTransport = lazy(() => import("./pages/EisTransport"));
const FischTransport = lazy(() => import("./pages/FischTransport"));
const FleischDoenerTransport = lazy(() => import("./pages/FleischDoenerTransport"));
const KuehlwagenKaufen = lazy(() => import("./pages/KuehlwagenKaufen"));
const KuehlwagenMieten = lazy(() => import("./pages/KuehlwagenMieten"));
const KuehltransporterMietenDeutschland = lazy(() => import("./pages/KuehltransporterMietenDeutschland"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));


// Lazy-load heavy providers only for routes that need them
const AuthProvider = lazy(() =>
  import("@/contexts/AuthContext").then(m => ({ default: m.AuthProvider }))
);
const HelmetProvider = lazy(() =>
  import("react-helmet-async").then(m => ({ default: m.HelmetProvider }))
);

/** Routes that need AuthProvider + HelmetProvider */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<main className="min-h-screen" />}>
    <HelmetProvider>
      <AuthProvider>{children}</AuthProvider>
    </HelmetProvider>
  </Suspense>
);

/** Routes that need HelmetProvider but NOT Auth (legal pages) */
const LegalRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="min-h-screen" />}>
    <HelmetProvider>{children}</HelmetProvider>
  </Suspense>
);

const LazyCookieBanner = lazy(() =>
  import("@/components/CookieBanner").then(m => ({ default: m.CookieBanner }))
);

/** Hook to defer mounting until browser is idle */
const useIdleMount = (delayMs = 2000) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(() => setReady(true), { timeout: delayMs });
      return () => cancelIdleCallback(id);
    } else {
      const id = setTimeout(() => setReady(true), delayMs);
      return () => clearTimeout(id);
    }
  }, [delayMs]);
  return ready;
};

const AppContent = () => {
  useAnalytics();
  const idleReady = useIdleMount();

  // Catch unhandled async errors (e.g. failed Supabase queries) so they don't crash the page
  useEffect(() => {
    const handler = (e: PromiseRejectionEvent) => {
      console.error("[Frostflotte] Unhandled rejection:", e.reason);
      e.preventDefault();
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);

  return (
    <BookingDockProvider>
      <NavigationProgress />
      <ScrollToTop />
      <DockedBookingBar />
      <Suspense fallback={<main className="min-h-screen" />}>
        <LanguageRoute>
          <Routes>
            <Route path="/" element={<LegalRoute><Index /></LegalRoute>} />

            {/* Auth-protected routes */}
            <Route path="/auth" element={<ProtectedRoute><Auth /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
            <Route path="/.lovable/oauth/consent" element={<ProtectedRoute><OAuthConsent /></ProtectedRoute>} />

            {/* Legal pages: need Helmet but not Auth */}
            <Route path="/impressum" element={<LegalRoute><Impressum /></LegalRoute>} />
            <Route path="/datenschutz" element={<LegalRoute><Datenschutz /></LegalRoute>} />
            <Route path="/agb" element={<LegalRoute><AGB /></LegalRoute>} />

            <Route path="/pharma-transport" element={<LegalRoute><PharmaTransport /></LegalRoute>} />
            <Route path="/catering-events" element={<LegalRoute><CateringEvents /></LegalRoute>} />
            <Route path="/lebensmittel-transport" element={<LegalRoute><LebensmittelTransport /></LegalRoute>} />
            <Route path="/ueber-uns" element={<LegalRoute><UeberUns /></LegalRoute>} />
            <Route path="/preise" element={<LegalRoute><PricingPage /></LegalRoute>} />
            <Route path="/mobile-kuehlung" element={<LegalRoute><MobileKuehlung /></LegalRoute>} />
            <Route path="/transporter-mieten" element={<LegalRoute><TransporterMieten /></LegalRoute>} />
            <Route path="/kleinbus-mieten-frankfurt" element={<LegalRoute><KleinbusMietenFrankfurt /></LegalRoute>} />
            <Route path="/kuehltransport-mit-fahrer" element={<LegalRoute><KuehltransportMitFahrer /></LegalRoute>} />
            <Route path="/vertrauen-sicherheit" element={<LegalRoute><Vertrauen /></LegalRoute>} />
            <Route path="/haccp-leitfaden" element={<LegalRoute><HaccpLeitfaden /></LegalRoute>} />
            <Route path="/standorte" element={<LegalRoute><Standorte /></LegalRoute>} />
            <Route path="/kuehltransporter-mieten/:stadt" element={<LegalRoute><StadtLanding /></LegalRoute>} />
            <Route path="/eis-transport" element={<LegalRoute><EisTransport /></LegalRoute>} />
            <Route path="/fisch-transport" element={<LegalRoute><FischTransport /></LegalRoute>} />
            <Route path="/fleisch-doener-transport" element={<LegalRoute><FleischDoenerTransport /></LegalRoute>} />
            <Route path="/kuehlwagen-kaufen" element={<LegalRoute><KuehlwagenKaufen /></LegalRoute>} />
            <Route path="/kuehlwagen-mieten" element={<LegalRoute><KuehlwagenMieten /></LegalRoute>} />
            <Route path="/kuehltransporter-mieten-deutschland" element={<LegalRoute><KuehltransporterMietenDeutschland /></LegalRoute>} />
            <Route path="/flachdach-kuehltransporter" element={<Navigate to="/" replace />} />
            {/* 301-Redirects von alten/entfernten URLs */}
            <Route path="/kuehlfahrzeug-mieten-frankfurt" element={<Navigate to="/" replace />} />
            <Route path="/kuehltransporter-mieten-frankfurt" element={<Navigate to="/" replace />} />
            <Route path="/tiefkuehlfahrzeug-mieten" element={<Navigate to="/" replace />} />
            <Route path="/tiefkuehltransporter-mieten" element={<Navigate to="/" replace />} />
            <Route path="/qrcode" element={<LegalRoute><QRCodePage /></LegalRoute>} />
            <Route path="/unsubscribe" element={<LegalRoute><Unsubscribe /></LegalRoute>} />

            {/* English routes */}
            <Route path="/en" element={<LegalRoute><Index /></LegalRoute>} />
            <Route path="/en/" element={<LegalRoute><Index /></LegalRoute>} />
            <Route path="/en/pharma-transport" element={<LegalRoute><PharmaTransport /></LegalRoute>} />
            <Route path="/en/catering-events" element={<LegalRoute><CateringEvents /></LegalRoute>} />
            <Route path="/en/food-transport" element={<LegalRoute><LebensmittelTransport /></LegalRoute>} />
            <Route path="/en/mobile-cooling" element={<LegalRoute><MobileKuehlung /></LegalRoute>} />
            <Route path="/en/van-rental" element={<LegalRoute><TransporterMieten /></LegalRoute>} />
            <Route path="/en/minibus-rental-frankfurt" element={<LegalRoute><KleinbusMietenFrankfurt /></LegalRoute>} />
            <Route path="/en/refrigerated-transport-with-driver" element={<LegalRoute><KuehltransportMitFahrer /></LegalRoute>} />
            <Route path="/en/about-us" element={<LegalRoute><UeberUns /></LegalRoute>} />
            <Route path="/en/locations" element={<LegalRoute><Standorte /></LegalRoute>} />
            <Route path="/en/refrigerated-van-rental/:stadt" element={<LegalRoute><StadtLanding /></LegalRoute>} />
            <Route path="/en/ice-transport" element={<LegalRoute><EisTransport /></LegalRoute>} />
            <Route path="/en/fish-transport" element={<LegalRoute><FischTransport /></LegalRoute>} />
            <Route path="/en/meat-doner-transport" element={<LegalRoute><FleischDoenerTransport /></LegalRoute>} />
            <Route path="/en/refrigerated-vans-for-sale" element={<LegalRoute><KuehlwagenKaufen /></LegalRoute>} />
            <Route path="/en/refrigerated-van-hire" element={<LegalRoute><KuehlwagenMieten /></LegalRoute>} />
            <Route path="/en/refrigerated-van-rental-germany" element={<LegalRoute><KuehltransporterMietenDeutschland /></LegalRoute>} />
            <Route path="/en/haccp-guide" element={<LegalRoute><HaccpLeitfaden /></LegalRoute>} />
            <Route path="/en/trust-security" element={<LegalRoute><Vertrauen /></LegalRoute>} />
            <Route path="/en/imprint" element={<LegalRoute><Impressum /></LegalRoute>} />
            <Route path="/en/privacy" element={<LegalRoute><Datenschutz /></LegalRoute>} />
            <Route path="/en/terms" element={<LegalRoute><AGB /></LegalRoute>} />
            <Route path="/en/qrcode" element={<LegalRoute><QRCodePage /></LegalRoute>} />
            <Route path="/en/unsubscribe" element={<LegalRoute><Unsubscribe /></LegalRoute>} />

            <Route path="*" element={<LegalRoute><NotFound /></LegalRoute>} />
          </Routes>
        </LanguageRoute>
      </Suspense>
      <Suspense fallback={null}>
        <LazyCookieBanner />
      </Suspense>
    </BookingDockProvider>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppContent />
        <IdleToasters />
      </BrowserRouter>
    </QueryClientProvider>
  </ErrorBoundary>
);

/** Mount toasters only after idle to remove them from critical chain */
const IdleToasters = () => {
  const ready = useIdleMount();
  if (!ready) return null;
  return (
    <div style={{ position: 'fixed', zIndex: 9999, pointerEvents: 'none' }} aria-hidden="false">
      <Suspense fallback={null}>
        <div style={{ pointerEvents: 'auto' }}>
          <RadixToaster />
          <SonnerToaster />
        </div>
      </Suspense>
    </div>
  );
};

export default App;
